import React, { useState, useEffect } from 'react';
import { useCalculatorStore } from '../../store';
import { SleepMode, SleepType } from '../../types';

export const Calculator: React.FC = () => {
  const {
    mode,
    targetTime,
    sleepType,
    fallAsleepTime,
    wakeUpBuffer,
    napDuration,
    result,
    isCalculating,
    setMode,
    setTargetTime,
    setSleepType,
    setFallAsleepTime,
    setWakeUpBuffer,
    setNapDuration,
    calculateSleep,
    clearResult,
    reset
  } = useCalculatorStore();

  const [showAdvanced, setShowAdvanced] = useState(false);

  // Sleep mode options
  const sleepModes: { value: SleepMode; label: string; icon: string; description: string }[] = [
    { value: 'bedtime', label: 'Calculate Bedtime', icon: '🛏️', description: 'I want to wake up at...' },
    { value: 'waketime', label: 'Calculate Wake Time', icon: '⏰', description: 'I want to go to bed at...' },
    { value: 'nap', label: 'Power Nap', icon: '😴', description: 'I want to nap now for...' }
  ];

  // Sleep type options
  const sleepTypes: { value: SleepType; label: string; icon: string; description: string; duration: number }[] = [
    { value: 'fast', label: 'Fast Sleeper', icon: '⚡', description: 'I fall asleep quickly (75min cycles)', duration: 75 },
    { value: 'normal', label: 'Normal Sleeper', icon: '😌', description: 'Average sleep pattern (90min cycles)', duration: 90 },
    { value: 'slow', label: 'Slow Sleeper', icon: '🐌', description: 'I take time to fall asleep (105min cycles)', duration: 105 }
  ];

  const formatTime = (time: { hours: number; minutes: number }) => {
    const hours12 = time.hours === 0 ? 12 : time.hours > 12 ? time.hours - 12 : time.hours;
    const ampm = time.hours >= 12 ? 'PM' : 'AM';
    const minutes = time.minutes.toString().padStart(2, '0');
    return `${hours12}:${minutes} ${ampm}`;
  };

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  const getQualityColor = (quality: string) => {
    switch (quality) {
      case 'excellent': return 'text-green-600 dark:text-green-400';
      case 'good': return 'text-blue-600 dark:text-blue-400';
      case 'fair': return 'text-yellow-600 dark:text-yellow-400';
      case 'poor': return 'text-red-600 dark:text-red-400';
      default: return 'text-slate-600 dark:text-slate-400';
    }
  };

  const getQualityIcon = (quality: string) => {
    switch (quality) {
      case 'excellent': return '🌟';
      case 'good': return '👍';
      case 'fair': return '👌';
      case 'poor': return '😴';
      default: return '💤';
    }
  };

  return (
    <div id="calculator-section" className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl p-6 md:p-8 mx-4 md:mx-0">
      <div className="mb-8">
        <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-2 flex items-center gap-3">
          🧮 Sleep Calculator
        </h2>
        <p className="text-slate-600 dark:text-slate-400">
          Calculate optimal sleep times based on your sleep cycles
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Input Section */}
        <div className="space-y-6">
          {/* Sleep Mode Selection */}
          <div>
            <label className="block text-sm font-semibold text-slate-900 dark:text-white mb-3">
              What do you want to calculate?
            </label>
            <div className="grid gap-3">
              {sleepModes.map((modeOption) => (
                <button
                  key={modeOption.value}
                  onClick={() => {
                    setMode(modeOption.value);
                    clearResult();
                  }}
                  className={`p-4 rounded-xl border-2 text-left transition-all duration-200 ${
                    mode === modeOption.value
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                      : 'border-slate-200 dark:border-slate-600 hover:border-slate-300 dark:hover:border-slate-500'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{modeOption.icon}</span>
                    <div>
                      <div className="font-medium text-slate-900 dark:text-white">
                        {modeOption.label}
                      </div>
                      <div className="text-sm text-slate-500 dark:text-slate-400">
                        {modeOption.description}
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Target Time Input */}
          <div>
            <label className="block text-sm font-semibold text-slate-900 dark:text-white mb-3">
              {mode === 'bedtime' ? 'Desired wake time' : mode === 'waketime' ? 'Desired bedtime' : 'Start nap at'}
            </label>
            <input
              type="time"
              value={targetTime}
              onChange={(e) => {
                setTargetTime(e.target.value);
                clearResult();
              }}
              className="w-full p-4 border border-slate-300 dark:border-slate-600 rounded-xl bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-lg font-mono focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Nap Duration (only for nap mode) */}
          {mode === 'nap' && (
            <div>
              <label className="block text-sm font-semibold text-slate-900 dark:text-white mb-3">
                Nap Duration (minutes)
              </label>
              <input
                type="range"
                min="10"
                max="120"
                step="5"
                value={napDuration}
                onChange={(e) => {
                  setNapDuration(parseInt(e.target.value));
                  clearResult();
                }}
                className="w-full"
              />
              <div className="flex justify-between text-sm text-slate-500 dark:text-slate-400 mt-1">
                <span>10min</span>
                <span className="font-medium text-slate-900 dark:text-white">{napDuration}min</span>
                <span>120min</span>
              </div>
            </div>
          )}

          {/* Sleep Type Selection */}
          <div>
            <label className="block text-sm font-semibold text-slate-900 dark:text-white mb-3">
              Sleep Pattern
            </label>
            <div className="grid gap-3">
              {sleepTypes.map((typeOption) => (
                <button
                  key={typeOption.value}
                  onClick={() => {
                    setSleepType(typeOption.value);
                    clearResult();
                  }}
                  className={`p-4 rounded-xl border-2 text-left transition-all duration-200 ${
                    sleepType === typeOption.value
                      ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                      : 'border-slate-200 dark:border-slate-600 hover:border-slate-300 dark:hover:border-slate-500'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{typeOption.icon}</span>
                    <div className="flex-1">
                      <div className="font-medium text-slate-900 dark:text-white">
                        {typeOption.label}
                      </div>
                      <div className="text-sm text-slate-500 dark:text-slate-400">
                        {typeOption.description}
                      </div>
                    </div>
                    <div className="text-sm font-mono text-slate-600 dark:text-slate-400">
                      {typeOption.duration}min
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Advanced Settings */}
          <div>
            <button
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="flex items-center gap-2 text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
            >
              <span>{showAdvanced ? '▼' : '▶'}</span>
              Advanced Settings
            </button>
            
            {showAdvanced && (
              <div className="mt-4 space-y-4 p-4 bg-slate-50 dark:bg-slate-700/50 rounded-xl">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    Time to fall asleep: {fallAsleepTime} minutes
                  </label>
                  <input
                    type="range"
                    min="5"
                    max="60"
                    step="5"
                    value={fallAsleepTime}
                    onChange={(e) => {
                      setFallAsleepTime(parseInt(e.target.value));
                      clearResult();
                    }}
                    className="w-full"
                  />
                </div>
                
                {mode !== 'nap' && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      Wake up buffer: {wakeUpBuffer} minutes
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="30"
                      step="5"
                      value={wakeUpBuffer}
                      onChange={(e) => {
                        setWakeUpBuffer(parseInt(e.target.value));
                        clearResult();
                      }}
                      className="w-full"
                    />
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              onClick={calculateSleep}
              disabled={isCalculating}
              className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 disabled:from-slate-400 disabled:to-slate-500 text-white font-semibold py-4 px-6 rounded-xl transition-all duration-200 transform hover:scale-105 disabled:scale-100 shadow-lg"
            >
              {isCalculating ? '⏳ Calculating...' : '🚀 Calculate'}
            </button>
            <button
              onClick={reset}
              className="px-6 py-4 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
            >
              🔄
            </button>
          </div>
        </div>

        {/* Results Section */}
        <div className="lg:pl-6">
          {result ? (
            <div className="space-y-6">
              <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-xl p-6">
                <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
                  🎯 Recommended Times
                </h3>
                
                <div className="grid gap-3 mb-6">
                  {result.recommendedTimes.slice(0, 3).map((time, index) => (
                    <div
                      key={index}
                      className={`p-4 rounded-lg ${
                        index === 1 
                          ? 'bg-white dark:bg-slate-800 border-2 border-blue-500 shadow-lg' 
                          : 'bg-white/60 dark:bg-slate-800/60'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="text-2xl font-mono font-bold text-slate-900 dark:text-white">
                          {formatTime(time)}
                        </div>
                        {index === 1 && (
                          <span className="text-xs bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-300 px-2 py-1 rounded-full font-medium">
                            RECOMMENDED
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-2 gap-4 text-center">
                  <div className="bg-white dark:bg-slate-800 rounded-lg p-4">
                    <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                      {result.cycleCount}
                    </div>
                    <div className="text-sm text-slate-600 dark:text-slate-400">Sleep Cycles</div>
                  </div>
                  <div className="bg-white dark:bg-slate-800 rounded-lg p-4">
                    <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                      {formatDuration(result.totalSleepDuration)}
                    </div>
                    <div className="text-sm text-slate-600 dark:text-slate-400">Total Sleep</div>
                  </div>
                </div>
              </div>

              {/* Sleep Quality */}
              <div className="bg-white dark:bg-slate-800 rounded-xl p-6 shadow-lg">
                <h4 className="text-lg font-semibold text-slate-900 dark:text-white mb-3 flex items-center gap-2">
                  {getQualityIcon(result.sleepQuality)} Sleep Quality
                </h4>
                <div className={`text-2xl font-bold mb-2 ${getQualityColor(result.sleepQuality)}`}>
                  {result.sleepQuality.charAt(0).toUpperCase() + result.sleepQuality.slice(1)}
                </div>
                <div className="text-sm text-slate-600 dark:text-slate-400">
                  Based on {result.cycleCount} complete sleep cycles
                </div>
              </div>

              {/* Tips */}
              {result.tips && result.tips.length > 0 && (
                <div className="bg-gradient-to-r from-green-50 to-blue-50 dark:from-green-900/20 dark:to-blue-900/20 rounded-xl p-6">
                  <h4 className="text-lg font-semibold text-slate-900 dark:text-white mb-3 flex items-center gap-2">
                    💡 Sleep Tips
                  </h4>
                  <ul className="space-y-2">
                    {result.tips.slice(0, 3).map((tip, index) => (
                      <li key={index} className="text-sm text-slate-600 dark:text-slate-400 flex items-start gap-2">
                        <span className="text-green-500 dark:text-green-400 mt-0.5">•</span>
                        {tip}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-slate-50 dark:bg-slate-700/50 rounded-xl p-8 text-center">
              <div className="text-6xl mb-4">💤</div>
              <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">
                Ready to Calculate
              </h3>
              <p className="text-slate-600 dark:text-slate-400">
                Set your preferences and click "Calculate" to get personalized sleep recommendations
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

console.log('✅ Calculator loaded successfully');